import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Rocket,
  ChevronLeft,
  ChevronRight,
  BookOpen,
} from "lucide-react";

import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  getCountFromServer,
} from "firebase/firestore";

import { db } from "../web/src/firebaseClient";

import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";

export default function InnovationHubMainPage() {
  const [active, setActive] = useState(0);
  const [startupCount, setStartupCount] = useState(0);
  const [paperCount, setPaperCount] = useState(0);
  const [banners, setBanners] = useState([]);
  const [isHovered, setIsHovered] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const q = query(collection(db, "banners"), orderBy("order", "asc"));
        const snapshot = await getDocs(q);
        const now = new Date();

        const data = snapshot.docs
          .map((docItem) => ({
            id: docItem.id,
            ...docItem.data(),
          }))
          .filter((banner) => {
            if (!banner.isActive) return false;

            const startDate = banner.startDate?.toDate?.();
            const endDate = banner.endDate?.toDate?.();

            if (startDate && now < startDate) return false;
            if (endDate && now > endDate) return false;

            return true;
          });

        setBanners(data);
      } catch (error) {
        console.error("Manşetler alınamadı:", error);
      }
    };

    fetchBanners();
  }, []);

  useEffect(() => {
    if (banners.length === 0 || isHovered) return;

    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [banners.length, isHovered]);

  useEffect(() => {
    if (banners.length <= 1) return;

    const nextIndex = (active + 1) % banners.length;
    const nextImageUrl = banners[nextIndex]?.imageUrl;

    if (!nextImageUrl) return;

    const img = new Image();
    img.src = nextImageUrl;
  }, [active, banners]);

  useEffect(() => {
    const fetchStartupCount = async () => {
      try {
        const q1 = query(
          collection(db, "starterApplications"),
          //where("status", "==", "approved")
        );

        const snapshot = await getCountFromServer(q1);
        setStartupCount(snapshot.data().count);
      } catch (error) {
        console.error("Girişim sayısı alınamadı:", error);
      }
    };

    fetchStartupCount();
  }, []);

  useEffect(() => {
    const fetchPaperCount = async () => {
      try {
        const q2 = query(
          collection(db, "researchProjects"),
          where("status", "==", "approved")
        );

        const snapshot = await getCountFromServer(q2);
        setPaperCount(snapshot.data().count);
      } catch (error) {
        console.error("Araştırma projesi sayısı alınamadı:", error);
      }
    };

    fetchPaperCount();
  }, []);

  const handleBannerClick = (url) => {
    if (!url) return;

    let cleanUrl = url.trim();
    if (!cleanUrl) return;

    if (cleanUrl.startsWith("http://") || cleanUrl.startsWith("https://")) {
      window.open(cleanUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (!cleanUrl.startsWith("/")) {
      cleanUrl = `/${cleanUrl}`;
    }

    navigate(cleanUrl);
  };

  const goPrev = (e) => {
    e.stopPropagation();
    if (banners.length === 0) return;
    setActive((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
  };

  const goNext = (e) => {
    e.stopPropagation();
    if (banners.length === 0) return;
    setActive((prev) => (prev + 1) % banners.length);
  };

  const activeBanner = banners[active];

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#eef2f7] via-[#f4f7fb] to-[#e6ecf5]"
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.16] bg-[linear-gradient(90deg,rgba(21,101,192,0.05)_1px,transparent_1px),linear-gradient(rgba(21,101,192,0.05)_1px,transparent_1px)] bg-[size:42px_42px]" />

      <div className="relative z-10">
        <InnovationHubHeader />

        <section className="px-4 py-4 sm:px-5 md:px-6 md:py-6">
          <div className="mx-auto grid max-w-[1500px] gap-4 lg:grid-cols-[0.85fr_1.8fr] lg:gap-6">
            {/* LEFT CARD */}
            <div className="order-2 rounded-[24px] bg-gradient-to-br from-[#1565C0]/30 via-white/80 to-[#C62828]/30 p-[1px] lg:order-1 lg:h-[480px] lg:rounded-[30px]">
              <div className="relative flex h-full min-h-[360px] flex-col overflow-hidden rounded-[23px] border border-white/70 bg-white/90 p-4 shadow-[0_20px_50px_rgba(21,101,192,0.12),0_8px_20px_rgba(0,0,0,0.06)] ring-1 ring-black/5 sm:min-h-[390px] lg:min-h-0">
                <div className="absolute -right-20 -top-20 h-52 w-52 rounded-full bg-[#1565C0]/20 blur-3xl" />
                <div className="absolute -bottom-20 -left-16 h-44 w-44 rounded-full bg-[#C62828]/20 blur-3xl" />

                <div className="relative z-10 mb-3 flex items-center gap-2">
                  <span className="h-[3px] w-[44px] rounded-full bg-[#1565C0] lg:w-[60px]" />
                  <span className="h-[3px] w-[30px] rounded-full bg-[#C62828] lg:w-[40px]" />
                </div>

                <div className="relative z-10 mb-3 inline-flex w-fit items-center gap-2 rounded-full border border-[#1565C0]/20 bg-[#1565C0]/10 px-3 py-1 text-xs font-semibold text-[#1565C0]">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-[#1565C0]" />
                  Innovation Hub
                </div>

                <div className="relative z-10">
                  <h1 className="leading-tight tracking-[-0.03em]">
                    <span className="block text-[16px] font-semibold text-[#173b7a] sm:text-[18px] lg:text-[20px]">
                      Fikirlerin girişimlere,
                    </span>

                    <span className="mt-1 block text-[16px] font-semibold text-[#C62828] sm:text-[18px] lg:text-[20px]">
                      araştırma projelerine ve yenilikçi çözümlere dönüştüğü
                    </span>

                    <span className="mt-1 block text-[16px] font-medium text-[#173b7a] sm:text-[18px] lg:text-[20px]">
                      ekosistemin bir parçası olmaya hazır mısın?
                    </span>
                  </h1>
                </div>

                <div className="relative z-10 mt-5 grid grid-cols-3 gap-2">
                  <MiniStep title="Fikir" subtitle="Keşfet" color="blue" />
                  <MiniStep title="Araştır" subtitle="Geliştir" color="red" />
                  <MiniStep title="Girişim" subtitle="Dönüştür" color="blue" />
                </div>

                <div className="relative z-10 mt-auto pt-5 space-y-3">
                  <ActionRow text="Araştırma projeleri başvurusu için">
                    <Link to="/research-project-application">
                      <MainBtn icon={BookOpen} />
                    </Link>
                  </ActionRow>

                  <ActionRow text="Girişim başvurusu için">
                    <Link to="/itk-starter">
                      <MainBtn icon={Rocket} />
                    </Link>
                  </ActionRow>
                </div>
              </div>
            </div>

            {/* RIGHT / BANNER CARD */}
            <div className="order-1 rounded-[24px] bg-gradient-to-br from-[#1565C0]/20 via-white/50 to-[#C62828]/20 p-[1px] lg:order-2 lg:h-[480px] lg:rounded-[30px]">
              <div
                className="relative h-[220px] overflow-hidden rounded-[23px] border border-white/70 bg-white/90 shadow-[0_20px_50px_rgba(21,101,192,0.12),0_8px_20px_rgba(0,0,0,0.06)] ring-1 ring-black/5 sm:h-[300px] md:h-[360px] lg:h-full"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {activeBanner ? (
                  <button
                    type="button"
                    onClick={() => handleBannerClick(activeBanner.targetUrl)}
                    className="block h-full w-full cursor-pointer"
                  >
                    <img
                      src={activeBanner.imageUrl}
                      alt={activeBanner.title || "Manşet"}
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      className="h-full w-full object-cover object-center transition-all duration-700"
                    />
                  </button>
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-sm text-gray-500">
                    Yayında manşet bulunmuyor.
                  </div>
                )}

                {banners.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={goPrev}
                      className="absolute left-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur transition hover:bg-black/50 md:left-4 md:h-11 md:w-11"
                    >
                      <ChevronLeft size={18} />
                    </button>

                    <button
                      type="button"
                      onClick={goNext}
                      className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur transition hover:bg-black/50 md:right-4 md:h-11 md:w-11"
                    >
                      <ChevronRight size={18} />
                    </button>

                    <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2 md:bottom-4">
                      {banners.map((_, i) => (
                        <button
                          type="button"
                          key={i}
                          onClick={() => setActive(i)}
                          className={`h-2 rounded-full transition-all ${
                            i === active ? "w-7 bg-[#1565C0]" : "w-2 bg-white"
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* STATS */}
        <section className="px-4 pb-10 sm:px-5 md:px-6 md:pb-14">
          <div className="mx-auto max-w-[1500px] rounded-[24px] bg-gradient-to-br from-[#1565C0]/16 via-white/40 to-[#C62828]/16 p-[1px] lg:rounded-[32px]">
            <div className="rounded-[23px] border border-white/70 bg-white/80 p-4 shadow-[0_18px_40px_rgba(21,101,192,0.08)] ring-1 ring-black/5 backdrop-blur-md lg:rounded-[31px] lg:p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-5">
                <Stat
                  icon={BookOpen}
                  value={paperCount}
                  label="Araştırma Projesi"
                  color="red"
                />

                <Stat icon={Rocket} value={startupCount} label="Girişim Başvurusu" />
              </div>
            </div>
          </div>
        </section>
      </div>

      <InnovationHubFooter />
    </main>
  );
}

function MiniStep({ title, subtitle, color }) {
  const isRed = color === "red";

  return (
    <div className="rounded-2xl border border-white/70 bg-white/70 p-2.5 text-center shadow-sm backdrop-blur">
      <div className={`text-base font-bold ${isRed ? "text-[#C62828]" : "text-[#1565C0]"}`}>
        {title}
      </div>

      <div className="text-[10px] text-gray-500">{subtitle}</div>
    </div>
  );
}

function ActionRow({ text, children }) {
  return (
    <div className="grid grid-cols-[1fr_20px_auto] items-center gap-2 sm:grid-cols-[190px_24px_auto] sm:gap-3">
      <div className="text-left text-[11px] font-semibold text-[#173b7a] sm:text-xs lg:text-sm">
        {text}
      </div>

      <div className="flex justify-center text-base font-bold text-[#C62828]">
        →
      </div>

      <div className="flex items-center">{children}</div>
    </div>
  );
}

function MainBtn({ icon: Icon }) {
  return (
    <button className="group flex items-center gap-2 rounded-lg border border-[#1565C0] bg-[#1565C0] px-2.5 py-2 shadow-[0_8px_18px_rgba(21,101,192,0.16)] transition duration-300 hover:-translate-y-0.5 hover:bg-[#0D47A1]">
      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white/15 text-white">
        <Icon size={15} />
      </div>
    </button>
  );
}

function Stat({ icon: Icon, value, label, color }) {
  const isRed = color === "red";

  return (
    <div className="flex items-center gap-3 rounded-[20px] border border-white/80 bg-white/90 p-4 shadow-[0_14px_30px_rgba(21,101,192,0.10),0_4px_10px_rgba(0,0,0,0.05)] ring-1 ring-black/5 backdrop-blur-md transition duration-300 hover:-translate-y-1 hover:shadow-[0_25px_60px_rgba(21,101,192,0.18)] lg:rounded-[22px] lg:p-5">
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-lg lg:h-11 lg:w-11 ${
          isRed ? "bg-[#C62828]/10 text-[#C62828]" : "bg-[#1565C0]/10 text-[#1565C0]"
        }`}
      >
        <Icon size={18} className="lg:h-5 lg:w-5" />
      </div>

      <div>
        <div
          className={`text-lg font-bold tracking-[-0.02em] lg:text-xl ${
            isRed ? "text-[#C62828]" : "text-[#1565C0]"
          }`}
        >
          {value}
        </div>

        <div className="text-[11px] font-medium tracking-[-0.01em] text-gray-500 lg:text-xs">
          {label}
        </div>
      </div>
    </div>
  );
}